import {
  isRecord,
  nullableString,
  request,
  requestNoContent,
  requireBoolean,
  requireNumber,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']

export type AssignableRole = Pick<Schemas['RoleDto'], 'roleId' | 'name' | 'description' | 'isSystem'>

function decodeAssignableRole(raw: unknown): AssignableRole {
  if (!isRecord(raw)) throw new Error('Malformed assignable role')
  return {
    roleId: requireString(raw.roleId, 'roleId'),
    name: requireString(raw.name, 'name'),
    description: nullableString(raw.description, 'description'),
    isSystem: requireBoolean(raw.isSystem, 'isSystem'),
  } satisfies AssignableRole
}

function decodeAssignableRoles(raw: unknown): AssignableRole[] {
  if (!isRecord(raw) || !Array.isArray(raw.items))
    throw new Error('Malformed assignable roles response')
  return raw.items.map(decodeAssignableRole)
}

export type RolePermission = Schemas['RolePermissionDto']

export type EntityRole = Schemas['RoleDto']

export type CreateEntityRoleInput = Omit<
  Schemas['CreateRoleRequest'],
  'name' | 'description' | 'permissionKeys'
> & Omit<
  Schemas['UpdateRoleRequest'],
  'name' | 'description' | 'permissionKeys'
> & {
  name: string
  description?: string
  permissionKeys: string[]
}

function decodeRolePermission(raw: unknown): RolePermission {
  if (!isRecord(raw)) throw new Error('Malformed role permission')
  return {
    key: requireString(raw.key, 'key'),
    name: requireString(raw.name, 'name'),
    group: requireString(raw.group, 'group'),
  } satisfies RolePermission
}

function decodeEntityRole(raw: unknown): EntityRole {
  if (!isRecord(raw) || !Array.isArray(raw.permissions))
    throw new Error('Malformed entity role')
  return {
    roleId: requireString(raw.roleId, 'roleId'),
    name: requireString(raw.name, 'name'),
    description: nullableString(raw.description, 'description'),
    isSystem: requireBoolean(raw.isSystem, 'isSystem'),
    permissions: raw.permissions.map(decodeRolePermission),
    ownerEntityId: nullableString(raw.ownerEntityId, 'ownerEntityId'),
    ownerEntityName: nullableString(raw.ownerEntityName, 'ownerEntityName'),
  } satisfies EntityRole
}

export type RoleAssignment = Replace<Schemas['RoleAssignmentDto'], 'subjectKind', {
  subjectKind: 'user' | 'machine' | 'group'
}>

export type RoleDetail = Replace<Schemas['RoleDetailResponse'], 'role' | 'assignments', {
  role: EntityRole
  assignments: RoleAssignment[]
}>

function decodeRoleAssignment(raw: unknown): RoleAssignment {
  if (!isRecord(raw)) throw new Error('Malformed role assignment')
  const kind = requireString(raw.subjectKind, 'subjectKind')
  if (kind !== 'user' && kind !== 'machine' && kind !== 'group')
    throw new Error(`Unexpected subjectKind "${kind}"`)
  return {
    entityId: requireString(raw.entityId, 'entityId'),
    entityName: requireString(raw.entityName, 'entityName'),
    subjectKind: kind,
    subjectId: requireString(raw.subjectId, 'subjectId'),
    subjectName: requireString(raw.subjectName, 'subjectName'),
    roleId: requireString(raw.roleId, 'roleId'),
    roleName: requireString(raw.roleName, 'roleName'),
    memberCount:
      raw.memberCount == null ? null : requireNumber(raw.memberCount, 'memberCount'),
  } satisfies RoleAssignment
}

function decodeRoleDetail(raw: unknown): RoleDetail {
  if (!isRecord(raw) || !Array.isArray(raw.assignments))
    throw new Error('Malformed role detail response')
  return {
    role: decodeEntityRole(raw.role),
    assignments: raw.assignments.map(decodeRoleAssignment),
  } satisfies RoleDetail
}

export type CatalogPermission = Schemas['CatalogPermissionDto']
export type CatalogCategory = Schemas['CatalogCategoryDto']

function decodeCatalogPermission(raw: unknown): CatalogPermission {
  if (!isRecord(raw)) throw new Error('Malformed catalog permission')
  return {
    key: requireString(raw.key, 'key'),
    name: requireString(raw.name, 'name'),
    description: requireString(raw.description, 'description'),
    canConfigure: requireBoolean(raw.canConfigure, 'canConfigure'),
  } satisfies CatalogPermission
}

function decodeCatalogCategory(raw: unknown): CatalogCategory {
  if (!isRecord(raw) || !Array.isArray(raw.permissions))
    throw new Error('Malformed catalog category')
  return {
    key: requireString(raw.key, 'key'),
    name: requireString(raw.name, 'name'),
    permissions: raw.permissions.map(decodeCatalogPermission),
  } satisfies CatalogCategory
}

function decodePermissionCatalog(raw: unknown): CatalogCategory[] {
  if (!isRecord(raw) || !Array.isArray(raw.categories))
    throw new Error('Malformed permission catalog response')
  return raw.categories.map(decodeCatalogCategory)
}

export const rolesApi = {
  getEntityRoles: (entityId: string) => request(`/api/v1/entities/${entityId}/roles`, decodeAssignableRoles),
  getRoleDetail: (entityId: string, roleId: string) => request(`/api/v1/entities/${entityId}/roles/${roleId}`, decodeRoleDetail),
  getPermissionCatalog: (entityId: string) => request(`/api/v1/entities/${entityId}/permissions`, decodePermissionCatalog),
  createEntityRole: (entityId: string, input: CreateEntityRoleInput) =>
    request(`/api/v1/entities/${entityId}/roles`, decodeEntityRole, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  updateEntityRole: (entityId: string, roleId: string, input: CreateEntityRoleInput) =>
    request(`/api/v1/entities/${entityId}/roles/${roleId}`, decodeEntityRole, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  deleteEntityRole: (entityId: string, roleId: string) =>
    requestNoContent(`/api/v1/entities/${entityId}/roles/${roleId}`, { method: 'DELETE' }),
}
